// Sales: Exitium's own pipeline for selling Exitium to furniture stores.
// Testing Store only (src/config/salesPipelineAccess.js). Reps share the
// Testing Store login, so each rep picks who they are at the top; the
// choice stays on their device.
//
// Four views: Playbook (home: the run card that hands you the next store,
// points, streak, scoreboard, badges, and the plan with the words), Today
// (what is due, by rep), Board (every store by stage) and All stores (the
// list: contacted or not, search, import, export). Logging an outcome on a
// store moves it to the next step and sets the next due date from the plan
// (salesPlan.js); salesGame.js scores it. Data: sql/sales-pipeline.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  DollarSign,
  Download,
  LayoutGrid,
  ListChecks,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Table2,
  Target,
  Upload,
  Users,
  X,
  Zap,
  CheckSquare,
  Square,
  Gamepad2,
  GitBranch,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { isSalesAdminRep } from '../../config/salesPipelineAccess';
import { STAGES, areaOf, currentAction, dueBucket, dueLabel, freshProspectFields, isDoNotContact, stageOf, tempOf, tierOf } from './salesPlan';
import { e164, endSprint, insertProspect, insertSprint, isMissingTable, loadAllTouches, loadPayouts, loadProspects, loadReps, loadSprints, prettyPhone, updateProspects } from './salesData';
import { sprintWindow } from './salesGame';
import { chime } from './salesSound';
import { DOT, Empty, Field, RepChip, Sheet, StagePill, TONE, TempChip, TierChip, btnGhost, btnPrimary, btnSmall, inputCls } from './salesUi';
import ProspectDrawer from './ProspectDrawer';
import ImportProspectsModal from './ImportProspectsModal';
import TeamModal from './TeamModal';
import PlaybookView from './PlaybookView';
import EarningsView from './EarningsView';
import SalesArena from './arena/SalesArena';
import FlowView from './FlowView';

const ME_KEY = 'exitium.sales.rep';
const VIEWS = [
  { id: 'playbook', label: 'Playbook', icon: Zap },
  { id: 'arena', label: 'Arena', icon: Gamepad2 },
  { id: 'flow', label: 'How it works', icon: GitBranch },
  { id: 'today', label: 'Today', icon: ListChecks },
  { id: 'board', label: 'Board', icon: LayoutGrid },
  { id: 'all', label: 'All stores', icon: Table2 },
  { id: 'earnings', label: 'Earnings', icon: DollarSign },
];
const BUCKET_LABEL = { overdue: 'Overdue', today: 'Today', soon: 'This week', later: 'Later' };
const BUCKET_TONE = {
  overdue: 'bg-red-50 text-red-700 border-red-200',
  today: 'bg-blue-50 text-blue-700 border-blue-200',
  soon: 'bg-slate-100 text-slate-600 border-slate-200',
  later: 'bg-slate-50 text-slate-500 border-slate-200',
  none: 'bg-slate-50 text-slate-400 border-slate-200',
};

const readMe = () => { try { return window.localStorage.getItem(ME_KEY) || ''; } catch { return ''; } };
const writeMe = (id) => { try { if (id) window.localStorage.setItem(ME_KEY, id); else window.localStorage.removeItem(ME_KEY); } catch { /* ignore */ } };

const stepText = (p) => {
  const a = currentAction(p);
  if (!a) return p.stage === 'lost' ? (p.lost_reason ? `Lost: ${p.lost_reason}` : 'Lost') : 'Nothing due';
  return a.isSequence ? `Step ${a.stepIndex + 1} of ${a.stepTotal} · ${a.label}` : a.label;
};

const placeText = (p) => [p.city, p.state].filter(Boolean).join(', ');

export default function SalesPipelineTab({ storeId, store, addNotification, client = supabase, hideHeader = false }) {
  const [reps, setReps] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [touches, setTouches] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [sprintsReady, setSprintsReady] = useState(false);
  const [dealsReady, setDealsReady] = useState(false);
  const [briefReady, setBriefReady] = useState(false);
  const [arenaReady, setArenaReady] = useState(false); // sql/sales-pipeline/13: map and Google facts columns
  const [loading, setLoading] = useState(true);
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('arena'); // the Arena is the sales page; the desk views sit behind it
  const [meId, setMeId] = useState(readMe);
  const [repFilter, setRepFilter] = useState(() => (readMe() ? 'mine' : 'all'));
  const [q, setQ] = useState('');
  const [stageFilter, setStageFilter] = useState('open');
  const [openId, setOpenId] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addPrefill, setAddPrefill] = useState(null); // from the Arena's Find box: a Google result, or just the typed name
  const [selected, setSelected] = useState(() => new Set());
  const [userEmail, setUserEmail] = useState('');

  const notify = useCallback((message, type = 'success') => {
    addNotification?.({ title: 'Sales', message, isEscalation: type === 'error' });
  }, [addNotification]);

  useEffect(() => {
    let alive = true;
    client?.auth?.getUser?.().then(({ data }) => { if (alive) setUserEmail(data?.user?.email || ''); }).catch(() => {});
    return () => { alive = false; };
  }, [client]);

  const load = useCallback(async (silent = false) => {
    if (!storeId) return;
    if (!silent) setLoading(true);
    try {
      const [r, p, t, q, s] = await Promise.all([loadReps(client, storeId), loadProspects(client, storeId), loadAllTouches(client, storeId), loadPayouts(client, storeId), loadSprints(client, storeId)]);
      setReps(r);
      setProspects(p.rows);
      setDealsReady(p.dealsReady);
      setBriefReady(!!p.briefReady);
      setArenaReady(!!p.arenaReady);
      setTouches(t);
      setPayouts(q);
      setSprints(s.rows);
      setSprintsReady(s.ready);
      setSetupNeeded(false);
      setLoadError('');
    } catch (e) {
      if (isMissingTable(e)) {
        setSetupNeeded(true);
      } else {
        console.error('sales: load failed', e);
        setLoadError(e?.message || 'Could not load the pipeline');
      }
    } finally {
      setLoading(false);
    }
  }, [client, storeId]);

  useEffect(() => { load(); }, [load]);

  // Reps share one list from different devices. Pull it again quietly
  // every minute and whenever the window comes back, as the backstop for
  // the live channel below (and the only feed until SQL 07 has run).
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(true); };
    const iv = setInterval(tick, 60000);
    window.addEventListener('focus', tick);
    return () => { clearInterval(iv); window.removeEventListener('focus', tick); };
  }, [load]);

  // Live: every touch, store change and sprint lands on every rep's screen
  // the moment it happens. A teammate's demo, pilot or close gets a toast
  // and a sound; a store writing back does too. Own touches are already in
  // state (same id), so they dedupe.
  const prospectsRef = useRef(prospects);
  const repsRef = useRef(reps);
  const meIdRef = useRef(meId);
  useEffect(() => { prospectsRef.current = prospects; }, [prospects]);
  useEffect(() => { repsRef.current = reps; }, [reps]);
  useEffect(() => { meIdRef.current = meId; }, [meId]);
  useEffect(() => {
    if (!storeId || typeof client?.channel !== 'function') return undefined;
    const where = { schema: 'public', filter: `store_id=eq.${storeId}` };
    const ch = client
      .channel(`sales-live-${storeId}`)
      .on('postgres_changes', { ...where, event: 'INSERT', table: 'sales_touches' }, ({ new: t }) => {
        if (!t?.id) return;
        setTouches((prev) => (prev.some((x) => x.id === t.id) ? prev : [...prev, t]));
        const p = prospectsRef.current.find((x) => x.id === t.prospect_id);
        const mine = meIdRef.current;
        const store = p?.name || 'a store';
        if (t.outcome === 'reply_received') {
          notify(`${store} wrote back${p?.rep_id && p.rep_id === mine ? '. It is at the top of your Today.' : p?.rep_id ? ` to ${repsRef.current.find((r) => r.id === p.rep_id)?.name || 'their rep'}.` : '.'}`);
          chime('reply');
          return;
        }
        if (t.outcome === 'demo_line') {
          notify(`${store} called the demo line${p?.rep_id && p.rep_id === mine ? '. It is at the top of your Today.' : ''}`);
          chime('reply');
          return;
        }
        if (t.kind === 'demo' && t.outcome === 'booked' && String(t.note || '').startsWith('Booked on the demo line')) {
          notify(`${store} booked a demo on the demo line${p?.rep_id && p.rep_id === mine ? '. It is yours.' : ''}`);
          chime('team');
          return;
        }
        if (t.rep_id && t.rep_id === mine) return;
        const who = t.rep_name || 'A teammate';
        const moved = t.to_stage && t.to_stage !== t.from_stage;
        if (moved && t.to_stage === 'customer') { notify(`${who} closed ${store}. New customer!`); chime('win'); }
        else if (moved && t.to_stage === 'pilot') { notify(`${who} signed a pilot at ${store}.`); chime('team'); }
        else if (moved && t.to_stage === 'demo') { notify(`${who} booked a demo at ${store}.`); chime('team'); }
      })
      .on('postgres_changes', { ...where, event: 'UPDATE', table: 'sales_prospects' }, ({ new: row }) => {
        if (row?.id) setProspects((prev) => prev.map((p) => (p.id === row.id ? { ...p, ...row } : p)));
      })
      .on('postgres_changes', { ...where, event: 'INSERT', table: 'sales_prospects' }, ({ new: row }) => {
        if (row?.id) setProspects((prev) => (prev.some((p) => p.id === row.id) ? prev : [...prev, row]));
      })
      .on('postgres_changes', { ...where, event: '*', table: 'sales_sprints' }, ({ eventType, new: row, old }) => {
        if (eventType === 'DELETE') { if (old?.id) setSprints((prev) => prev.filter((s) => s.id !== old.id)); return; }
        if (!row?.id) return;
        setSprints((prev) => (prev.some((s) => s.id === row.id) ? prev.map((s) => (s.id === row.id ? { ...s, ...row } : s)) : [row, ...prev]));
        if (eventType === 'INSERT') { notify(`${row.name} is on${row.prize ? `. Prize: ${row.prize}` : ''}.`); chime('bell'); }
      })
      .subscribe();
    return () => { try { client.removeChannel(ch); } catch { /* ignore */ } };
  }, [client, storeId, notify]);

  // Opened from a push ("<store> wrote back"): the URL carries the store,
  // or the service worker posts it when the dashboard is already open.
  useEffect(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('prospect');
      if (id) setOpenId(id);
    } catch { /* ignore */ }
    const sw = navigator.serviceWorker;
    if (!sw?.addEventListener) return undefined;
    const onMsg = (e) => { if (e.data?.type === 'OPEN_PROSPECT' && e.data.prospectId) { setOpenId(e.data.prospectId); setView('playbook'); } };
    sw.addEventListener('message', onMsg);
    return () => sw.removeEventListener('message', onMsg);
  }, []);

  const me = useMemo(() => reps.find((r) => r.id === meId) || null, [reps, meId]);
  const repById = useMemo(() => Object.fromEntries(reps.map((r) => [r.id, r])), [reps]);
  const activeReps = useMemo(() => reps.filter((r) => r.active !== false), [reps]);
  const isAdmin = isSalesAdminRep(me);
  const openImport = isAdmin ? () => setShowImport(true) : null;

  const pickMe = (id) => {
    setMeId(id);
    writeMe(id);
    setRepFilter(id ? 'mine' : 'all');
  };

  // ---------- derived ----------
  const now = new Date();
  const byRep = useCallback((p) => repFilter === 'all' || !me || p.rep_id === me.id, [repFilter, me]);

  const counts = useMemo(() => {
    const c = { total: prospects.length, dueNow: 0, overdue: 0 };
    for (const s of STAGES) c[s.key] = 0;
    for (const p of prospects) {
      c[p.stage] = (c[p.stage] || 0) + 1;
      if (p.stage !== 'lost' && p.next_due_at) {
        const b = dueBucket(p.next_due_at, now);
        if (b === 'overdue') { c.overdue += 1; c.dueNow += 1; }
        if (b === 'today') c.dueNow += 1;
      }
    }
    return c;
  }, [prospects]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayGroups = useMemo(() => {
    const g = { overdue: [], today: [], soon: [], later: [] };
    for (const p of prospects) {
      if (p.stage === 'lost' || !p.next_due_at || !byRep(p)) continue;
      const b = dueBucket(p.next_due_at, now);
      if (g[b]) g[b].push(p);
    }
    const byDue = (a, b) => new Date(a.next_due_at) - new Date(b.next_due_at);
    for (const k of Object.keys(g)) g[k].sort(byDue);
    return g;
  }, [prospects, byRep]); // eslint-disable-line react-hooks/exhaustive-deps

  const listRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const nd = needle.replace(/\D/g, '');
    return prospects
      .filter((p) => {
        if (stageFilter === 'open') { if (p.stage === 'lost' || p.stage === 'customer') return false; }
        else if (stageFilter !== 'all' && p.stage !== stageFilter) return false;
        if (!byRep(p)) return false;
        if (!needle) return true;
        return (
          (p.name || '').toLowerCase().includes(needle) ||
          (p.owner_name || '').toLowerCase().includes(needle) ||
          (p.city || '').toLowerCase().includes(needle) ||
          (p.tags || []).some((t) => String(t).toLowerCase().includes(needle)) ||
          (nd.length >= 3 && String(p.phone || '').replace(/\D/g, '').includes(nd))
        );
      })
      .sort((a, b) => {
        const ad = a.next_due_at ? new Date(a.next_due_at).getTime() : Infinity;
        const bd = b.next_due_at ? new Date(b.next_due_at).getTime() : Infinity;
        return ad - bd || (a.name || '').localeCompare(b.name || '');
      });
  }, [prospects, q, stageFilter, byRep]);

  const openProspect = openId ? prospects.find((p) => p.id === openId) : null;

  // ---------- mutations from children ----------
  const onChanged = useCallback((row) => {
    setProspects((prev) => prev.map((p) => (p.id === row.id ? { ...p, ...row } : p)));
  }, []);
  const onDeleted = useCallback((id) => {
    setProspects((prev) => prev.filter((p) => p.id !== id));
    setTouches((prev) => prev.filter((t) => t.prospect_id !== id));
    setOpenId('');
  }, []);
  const onTouch = useCallback((t) => {
    if (t) setTouches((prev) => (prev.some((x) => x.id === t.id) ? prev : [...prev, t]));
  }, []);
  const onImported = useCallback((rows) => {
    setProspects((prev) => [...prev, ...rows]);
    setShowImport(false);
    setView('all');
    setStageFilter('new');
    notify(`${rows.length} store${rows.length === 1 ? '' : 's'} added to the list`);
  }, [notify]);

  // Sprints: Phillip starts one from a preset; anyone can see it. The
  // insert comes back over the live channel too, so it dedupes by id.
  const startSprint = useCallback(async (preset, prize) => {
    try {
      const row = await insertSprint(client, { store_id: storeId, name: preset.name, metric: preset.metric, ...sprintWindow(preset), prize: prize?.trim() || null, created_by: me?.id || null });
      setSprints((prev) => (prev.some((s) => s.id === row.id) ? prev : [row, ...prev]));
      notify(`${row.name} is on${row.prize ? `. Prize: ${row.prize}` : ''}.`);
      chime('bell');
      return row;
    } catch (e) {
      console.error('sales: sprint failed', e);
      notify(isMissingTable(e) ? 'Sprints are not set up yet. Run sql/sales-pipeline/07.' : 'Could not start the sprint.', 'error');
      return null;
    }
  }, [client, storeId, me, notify]);
  const stopSprint = useCallback(async (id) => {
    try {
      const row = await endSprint(client, id);
      setSprints((prev) => prev.map((s) => (s.id === row.id ? row : s)));
      notify('Sprint ended. The standings stay up for a day.');
    } catch (e) {
      console.error('sales: end sprint failed', e);
      notify('Could not end the sprint.', 'error');
    }
  }, [client, notify]);

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearSelect = () => setSelected(new Set());
  const assignSelected = async (repId) => {
    const ids = [...selected];
    try {
      const rows = await updateProspects(client, ids, { rep_id: repId || null });
      setProspects((prev) => { const m = Object.fromEntries(rows.map((r) => [r.id, r])); return prev.map((p) => m[p.id] || p); });
      notify(`${ids.length} store${ids.length === 1 ? '' : 's'} ${repId ? `assigned to ${repById[repId]?.name || 'rep'}` : 'unassigned'}`);
      clearSelect();
    } catch (e) {
      console.error('sales: assign failed', e);
      notify('Could not assign those stores', 'error');
    }
  };

  const exportCsv = () => {
    const head = ['store', 'owner', 'phone', 'email', 'address', 'city', 'state', 'zip', 'website', 'social', 'area', 'stage', 'next_action', 'next_due', 'rep', 'touches', 'last_touch', 'notes', 'lost_reason'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = listRows.map((p) => [p.name, p.owner_name, p.phone, p.email, p.address, p.city, p.state, p.zip, p.website, p.social, (p.tags || []).join('; '), stageOf(p.stage).label, stepText(p), p.next_due_at || '', repById[p.rep_id]?.name || '', p.touch_count || 0, p.last_touch_at || '', p.notes, p.lost_reason].map(esc).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `exitium-pipeline-${stageFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ---------- render ----------
  if (setupNeeded) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900">The sales tables are not set up yet</h2>
              <p className="text-sm text-slate-700 mt-1">Run the SQL below in the Supabase SQL editor once, then reload. It adds three new tables and touches nothing else.</p>
              <pre className="mt-3 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 overflow-x-auto">sql/sales-pipeline/01_create_sales_pipeline.sql</pre>
              <button type="button" onClick={() => load()} className={`${btnGhost} mt-4`}><RefreshCw className="w-4 h-4" /> Check again</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          {!hideHeader && (
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">Sales</h1>
                <p className="text-sm text-slate-500 truncate">
                  {counts.total} store{counts.total === 1 ? '' : 's'} on the list · {counts.new} not contacted · {counts.dueNow} due now
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <MePicker reps={activeReps} me={me} onPick={pickMe} onTeam={() => setShowTeam(true)} />
            <button type="button" onClick={() => load()} className={btnGhost} title="Reload"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
            {isAdmin && <button type="button" onClick={() => setShowImport(true)} className={btnGhost} title="Import a list of stores from a CSV or spreadsheet"><Upload className="w-4 h-4" /> <span className="hidden sm:inline">Import list</span></button>}
            <button type="button" onClick={() => setShowAdd(true)} className={btnPrimary} title="Add one store by hand"><Plus className="w-4 h-4" /> Add store</button>
            <button type="button" onClick={() => setView('arena')} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white text-sm font-bold shadow-md" title="The pipeline as a game, on the map"><Gamepad2 className="w-4 h-4" /> Enter the Arena</button>
          </div>
        </div>

        {loadError && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {loadError}
            <button type="button" onClick={() => load()} className="ml-auto text-xs font-semibold underline">Try again</button>
          </div>
        )}

        {/* Stat tiles */}
        <div className="mt-5 grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
          <Tile label="Due now" value={counts.dueNow} sub={counts.overdue ? `${counts.overdue} overdue` : 'nothing late'} tone={counts.overdue ? 'red' : 'blue'} onClick={() => setView('today')} active={view === 'today'} />
          <Tile label="Not contacted" value={counts.new} tone="slate" onClick={() => { setView('all'); setStageFilter('new'); }} active={view === 'all' && stageFilter === 'new'} />
          <Tile label="Working" value={counts.working} tone="blue" onClick={() => { setView('all'); setStageFilter('working'); }} active={view === 'all' && stageFilter === 'working'} />
          <Tile label="Demos" value={counts.demo} tone="violet" onClick={() => { setView('all'); setStageFilter('demo'); }} active={view === 'all' && stageFilter === 'demo'} />
          <Tile label="Pilots" value={counts.pilot} tone="amber" onClick={() => { setView('all'); setStageFilter('pilot'); }} active={view === 'all' && stageFilter === 'pilot'} />
          <Tile label="Customers" value={counts.customer} tone="emerald" onClick={() => { setView('all'); setStageFilter('customer'); }} active={view === 'all' && stageFilter === 'customer'} />
        </div>

        {/* View switch + rep filter */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1 overflow-x-auto no-scrollbar max-w-full">
            {VIEWS.map((v) => (
              <button key={v.id} type="button" onClick={() => setView(v.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap transition-all ${view === v.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                <v.icon className="w-4 h-4" /> {v.label}
              </button>
            ))}
          </div>
          {me && view !== 'playbook' && view !== 'earnings' && view !== 'flow' && (
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button type="button" onClick={() => setRepFilter('mine')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${repFilter === 'mine' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>My stores</button>
              <button type="button" onClick={() => setRepFilter('all')} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${repFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Everyone</button>
            </div>
          )}
        </div>

        {!me && activeReps.length > 0 && view !== 'playbook' && view !== 'earnings' && view !== 'flow' && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex flex-wrap items-center gap-2">
            <Users className="w-4 h-4" /> Pick who you are at the top so Today shows your stores and your name goes on every call you log.
          </div>
        )}
        {reps.length === 0 && !loading && view !== 'playbook' && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex flex-wrap items-center gap-2">
            <Users className="w-4 h-4" /> Add the team first, then import the store list.
            <button type="button" onClick={() => setShowTeam(true)} className="ml-auto text-xs font-bold underline">Add the team</button>
          </div>
        )}

        {/* Views */}
        <div className="mt-4">
          {loading && prospects.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
          ) : view === 'today' ? (
            <TodayView groups={todayGroups} repById={repById} onOpen={setOpenId} total={prospects.length} onImport={openImport} me={me} repFilter={repFilter} />
          ) : view === 'flow' ? (
            <FlowView />
          ) : view === 'board' ? (
            <BoardView prospects={prospects.filter(byRep)} repById={repById} onOpen={setOpenId} />
          ) : view === 'all' ? (
            <AllView
              rows={listRows}
              counts={counts}
              q={q}
              setQ={setQ}
              stageFilter={stageFilter}
              setStageFilter={setStageFilter}
              repById={repById}
              reps={activeReps}
              onOpen={setOpenId}
              selected={selected}
              toggleSelect={toggleSelect}
              clearSelect={clearSelect}
              selectAll={() => setSelected(new Set(listRows.map((r) => r.id)))}
              assignSelected={assignSelected}
              exportCsv={exportCsv}
              onImport={openImport}
              onAdd={() => setShowAdd(true)}
            />
          ) : view === 'earnings' ? (
            <EarningsView
              prospects={prospects}
              reps={activeReps}
              repById={repById}
              me={me}
              payouts={payouts}
              onPayoutsChange={setPayouts}
              client={client}
              storeId={storeId}
              onOpen={setOpenId}
              notify={notify}
              dealsReady={dealsReady}
              onRetry={() => load()}
            />
          ) : (
            <PlaybookView
              sprints={sprints}
              sprintsReady={sprintsReady}
              onSprintStart={startSprint}
              onSprintEnd={stopSprint}
              isAdmin={isAdmin}
              prospects={prospects}
              touches={touches}
              reps={activeReps}
              repById={repById}
              me={me}
              client={client}
              storeId={storeId}
              onChange={onChanged}
              onTouch={onTouch}
              onOpen={setOpenId}
              notify={notify}
              onPickMe={pickMe}
              onTeam={() => setShowTeam(true)}
              onImport={openImport}
              onAdd={() => setShowAdd(true)}
              dealsReady={dealsReady}
              briefReady={briefReady}
            />
          )}
        </div>
      </div>

      {view === 'arena' && (
        <SalesArena
          prospects={prospects}
          touches={touches}
          reps={activeReps}
          repById={repById}
          me={me}
          client={client}
          storeId={storeId}
          onChange={onChanged}
          onTouch={onTouch}
          onOpen={setOpenId}
          notify={notify}
          onPickMe={pickMe}
          onExit={() => setView('playbook')}
          onAddStore={(prefill) => { setAddPrefill(prefill && typeof prefill.name === 'string' ? prefill : null); setShowAdd(true); }}
          dealsReady={dealsReady}
          briefReady={briefReady}
          arenaReady={arenaReady}
        />
      )}
      {openProspect && (
        <ProspectDrawer
          client={client}
          storeId={storeId}
          prospect={openProspect}
          reps={activeReps}
          repById={repById}
          me={me}
          userEmail={userEmail}
          onChange={onChanged}
          onTouch={onTouch}
          onDelete={onDeleted}
          dealsReady={dealsReady}
          briefReady={briefReady}
          onClose={() => setOpenId('')}
          notify={notify}
        />
      )}
      {showImport && (
        <ImportProspectsModal client={client} storeId={storeId} existing={prospects} reps={activeReps} me={me} userEmail={userEmail} onClose={() => setShowImport(false)} onImported={onImported} notify={notify} />
      )}
      {showTeam && (
        <TeamModal client={client} storeId={storeId} reps={reps} onChange={setReps} onClose={() => setShowTeam(false)} notify={notify} meId={meId} onPickMe={pickMe} />
      )}
      {showAdd && (
        <AddProspectSheet client={client} storeId={storeId} reps={activeReps} me={me} userEmail={userEmail} existing={prospects} initial={addPrefill} onClose={() => { setShowAdd(false); setAddPrefill(null); }} notify={notify} onAdded={(row) => { setProspects((prev) => [...prev, row]); setShowAdd(false); setOpenId(row.id); }} />
      )}
    </div>
  );
}

/* ---------------- header bits ---------------- */

function MePicker({ reps, me, onPick, onTeam }) {
  return (
    <div className="flex items-center gap-1.5 h-10 pl-2 pr-1 rounded-lg border border-slate-200 bg-white">
      <RepChip rep={me} size="md" />
      <select value={me?.id || ''} onChange={(e) => onPick(e.target.value)} className="h-8 bg-transparent text-sm font-semibold text-slate-800 focus:outline-none max-w-[140px]" title="Who is using this device" aria-label="Who are you">
        <option value="">{reps.length ? 'Who are you?' : 'No team yet'}</option>
        {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <button type="button" onClick={onTeam} className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500" title="The team: add or edit reps" aria-label="Team">
        <Users className="w-4 h-4" />
      </button>
    </div>
  );
}

function Tile({ label, value, sub, tone, onClick, active }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3 bg-white transition-all ${active ? 'border-blue-400 ring-2 ring-blue-500/15' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${DOT[tone] || DOT.slate}`} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 leading-tight mt-1 whitespace-nowrap tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
    </button>
  );
}

/* ---------------- Today ---------------- */

function TodayView({ groups, repById, onOpen, total, onImport, me, repFilter }) {
  const any = groups.overdue.length + groups.today.length + groups.soon.length + groups.later.length;
  if (total === 0) {
    return <Empty icon={ListChecks} title="Nothing on the list yet" body={onImport ? 'Import the store list, or add one store, and the first call shows up here as due today.' : 'Phillip loads the store list. Once it is in, your first calls show up here.'} action={onImport ? <button type="button" onClick={onImport} className={btnPrimary}><Upload className="w-4 h-4" /> Import list</button> : undefined} />;
  }
  if (any === 0) {
    return <Empty icon={CalendarClock} title={me && repFilter === 'mine' ? 'Nothing due on your stores' : 'Nothing due'} body="Every store is either waiting on its next date, won, parked or lost." />;
  }
  return (
    <div className="space-y-6">
      {['overdue', 'today', 'soon', 'later'].map((k) => {
        const rows = groups[k];
        if (!rows.length) return null;
        return (
          <section key={k}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${BUCKET_TONE[k]}`}>{BUCKET_LABEL[k]}</span>
              <span className="text-xs text-slate-400">{rows.length}</span>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
              {rows.map((p) => <ActionRow key={p.id} p={p} rep={repById[p.rep_id]} onOpen={() => onOpen(p.id)} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ActionRow({ p, rep, onOpen }) {
  const s = stageOf(p.stage);
  const a = currentAction(p);
  const tel = e164(p.phone);
  const b = dueBucket(p.next_due_at);
  return (
    <div className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-slate-50/70">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT[s.tone]}`} title={s.label} />
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-slate-900 truncate">{p.name}</span>
          {placeText(p) && <span className="text-xs text-slate-400 truncate hidden sm:inline">{placeText(p)}</span>}
        </div>
        <div className="text-sm text-slate-600 truncate mt-0.5">
          {a?.isSequence && <span className="text-[11px] font-bold text-slate-400 mr-1.5">{a.stepIndex + 1}/{a.stepTotal}</span>}
          {a?.label || stepText(p)}
        </div>
      </button>
      <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold whitespace-nowrap ${BUCKET_TONE[b]}`}>{dueLabel(p.next_due_at)}</span>
      <RepChip rep={rep} />
      {tel && (a?.kind === 'call' || a?.kind === 'text') && (
        <a href={a.kind === 'text' ? `sms:${tel}` : `tel:${tel}`} className={`${btnSmall} hidden sm:inline-flex`} title={`${a.kind === 'text' ? 'Text' : 'Call'} ${prettyPhone(p.phone)}`}>
          <Phone className="w-3.5 h-3.5" /> {a.kind === 'text' ? 'Text' : 'Call'}
        </a>
      )}
      <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold" title="Open the store and log what happened">
        Log <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ---------------- Board ---------------- */

function BoardView({ prospects, repById, onOpen }) {
  const cols = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const p of prospects) (m[p.stage] || m.new).push(p);
    const byDue = (a, b) => {
      const ad = a.next_due_at ? new Date(a.next_due_at).getTime() : Infinity;
      const bd = b.next_due_at ? new Date(b.next_due_at).getTime() : Infinity;
      return ad - bd;
    };
    for (const k of Object.keys(m)) m[k].sort(byDue);
    return m;
  }, [prospects]);
  if (!prospects.length) return <Empty icon={LayoutGrid} title="Nothing on the board" body="Stores show up here by stage once they are on the list." />;
  return (
    <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x">
      {STAGES.map((s) => (
        <div key={s.key} className="w-[272px] flex-shrink-0 snap-start max-w-none" style={{ maxWidth: 'none' }}>
          <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl border border-b-0 ${TONE[s.tone]}`}>
            <span className="text-xs font-bold uppercase tracking-wider">{s.label}</span>
            <span className="text-xs font-bold">{cols[s.key].length}</span>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-b-xl p-2 space-y-2 min-h-[120px] max-h-[65vh] overflow-y-auto">
            {cols[s.key].length === 0 && <p className="text-xs text-slate-400 text-center py-6 px-2">{s.hint}</p>}
            {cols[s.key].map((p) => <Card key={p.id} p={p} rep={repById[p.rep_id]} onOpen={() => onOpen(p.id)} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Card({ p, rep, onOpen }) {
  const b = dueBucket(p.next_due_at);
  const a = currentAction(p);
  return (
    <button type="button" onClick={onOpen} className="w-full text-left bg-white border border-slate-200 rounded-lg px-3 py-2.5 hover:border-blue-300 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-slate-900 truncate">{p.name}</div>
          {placeText(p) && <div className="text-[11px] text-slate-400 truncate">{placeText(p)}</div>}
        </div>
        <RepChip rep={rep} />
      </div>
      {a && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-600 truncate">{a.isSequence ? `${a.stepIndex + 1}/${a.stepTotal} · ${a.short || a.label}` : a.label}</span>
          {p.next_due_at && <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-bold whitespace-nowrap ${BUCKET_TONE[b]}`}>{dueLabel(p.next_due_at)}</span>}
        </div>
      )}
      {p.stage === 'lost' && p.lost_reason && <div className="mt-2 text-xs text-red-700 truncate">{p.lost_reason}</div>}
    </button>
  );
}

/* ---------------- All stores ---------------- */

const LIST_FILTERS = [
  { id: 'open', label: 'In play' },
  { id: 'new', label: 'Not contacted' },
  { id: 'working', label: 'Working' },
  { id: 'demo', label: 'Demo' },
  { id: 'pilot', label: 'Pilot' },
  { id: 'customer', label: 'Customers' },
  { id: 'parked', label: 'Parked' },
  { id: 'lost', label: 'Lost' },
  { id: 'all', label: 'All' },
];

function AllView({ rows, counts, q, setQ, stageFilter, setStageFilter, repById, reps, onOpen, selected, toggleSelect, clearSelect, selectAll, assignSelected, exportCsv, onImport, onAdd }) {
  const [assignTo, setAssignTo] = useState('');
  const countFor = (id) => (id === 'all' ? counts.total : id === 'open' ? counts.total - (counts.lost || 0) - (counts.customer || 0) : counts[id] || 0);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-slate-100 rounded-lg p-1 overflow-x-auto no-scrollbar max-w-full">
          {LIST_FILTERS.map((f) => (
            <button key={f.id} type="button" onClick={() => setStageFilter(f.id)} className={`px-3 py-1.5 rounded-md text-sm font-semibold whitespace-nowrap transition-all ${stageFilter === f.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
              {f.label} <span className="text-slate-400 font-medium">{countFor(f.id)}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search store, owner, city, area or number" className={`${inputCls} pl-10 pr-9`} />
          {q && (
            <button type="button" onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200" aria-label="Clear search"><X className="w-3 h-3 text-slate-500" /></button>
          )}
        </div>
        <button type="button" onClick={exportCsv} disabled={!rows.length} className={btnGhost} title="Download the rows shown as a CSV file"><Download className="w-4 h-4" /> <span className="hidden sm:inline">Export</span></button>
      </div>

      {selected.size > 0 && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-blue-800">{selected.size} selected</span>
          <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="h-9 px-2 rounded-lg border border-blue-200 bg-white text-sm">
            <option value="">Assign to…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            <option value="__none">Nobody</option>
          </select>
          <button type="button" disabled={!assignTo} onClick={() => assignSelected(assignTo === '__none' ? '' : assignTo)} className={`${btnSmall} h-9`}>Apply</button>
          <button type="button" onClick={clearSelect} className="ml-auto text-xs font-semibold text-blue-700 underline">Clear</button>
        </div>
      )}

      <div className="mt-3">
        {rows.length === 0 ? (
          <Empty icon={Table2} title={q ? 'No matches' : stageFilter === 'new' ? 'Every store has been contacted' : 'No stores here'} body={counts.total === 0 ? (onImport ? 'Import a list, or add one store by hand.' : 'Phillip loads the store list.') : undefined} action={counts.total === 0 ? <div className="flex gap-2">{onImport && <button type="button" onClick={onImport} className={btnPrimary}><Upload className="w-4 h-4" /> Import list</button>}<button type="button" onClick={onAdd} className={btnGhost}><Plus className="w-4 h-4" /> Add store</button></div> : undefined} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-10 px-3 py-2.5">
                    <button type="button" onClick={selected.size === rows.length ? clearSelect : selectAll} className="text-slate-400 hover:text-slate-700 flex" aria-label="Select all">
                      {selected.size === rows.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="text-left font-semibold px-3 py-2.5">Store</th>
                  <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">Phone</th>
                  <th className="text-left font-semibold px-3 py-2.5">Stage</th>
                  <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">Next</th>
                  <th className="text-left font-semibold px-3 py-2.5 hidden sm:table-cell">Due</th>
                  <th className="text-left font-semibold px-3 py-2.5">Rep</th>
                  <th className="px-2 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => {
                  const b = dueBucket(p.next_due_at);
                  const sel = selected.has(p.id);
                  return (
                    <tr key={p.id} className={`${sel ? 'bg-blue-50/40' : 'hover:bg-slate-50/70'} cursor-pointer`} onClick={() => onOpen(p.id)}>
                      <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}>
                        <button type="button" className="text-slate-400 hover:text-slate-700 flex" aria-label={sel ? 'Unselect' : 'Select'}>
                          {sel ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-3 min-w-0">
                        <div className="font-semibold text-slate-900 truncate max-w-[220px] sm:max-w-none">{p.name}</div>
                        <div className="text-xs text-slate-400 truncate">{[p.owner_name, placeText(p)].filter(Boolean).join(' · ') || (p.phone ? prettyPhone(p.phone) : '')}{areaOf(p) && <span className="ml-1.5 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-500">{areaOf(p)}</span>}</div>
                        {(tempOf(p) || tierOf(p)) && <div className="mt-1 flex items-center gap-1"><TempChip prospect={p} small /><TierChip prospect={p} small /></div>}
                      </td>
                      <td className="px-3 py-3 font-mono text-slate-700 hidden md:table-cell whitespace-nowrap">{p.phone ? prettyPhone(p.phone) : <span className="text-slate-300">none</span>}</td>
                      <td className="px-3 py-3">{isDoNotContact(p) ? <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-red-300 bg-red-50 text-[11px] font-bold text-red-700 whitespace-nowrap">Do not contact</span> : <StagePill stage={p.stage} />}</td>
                      <td className="px-3 py-3 text-slate-600 hidden lg:table-cell"><span className="block truncate max-w-[280px]">{stepText(p)}</span></td>
                      <td className="px-3 py-3 hidden sm:table-cell">{p.next_due_at && p.stage !== 'lost' ? <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold whitespace-nowrap ${BUCKET_TONE[b]}`}>{dueLabel(p.next_due_at)}</span> : <span className="text-slate-300">none</span>}</td>
                      <td className="px-3 py-3"><RepChip rep={repById[p.rep_id]} /></td>
                      <td className="px-2 py-3 text-slate-300"><ChevronRight className="w-4 h-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Add one store ---------------- */

function AddProspectSheet({ client, storeId, reps, me, userEmail, existing, initial = null, onClose, onAdded, notify }) {
  const [f, setF] = useState(() => ({ name: '', owner_name: '', phone: '', email: '', address: '', city: '', state: '', zip: '', website: '', social: '', notes: '', rep_id: me?.id || '', ...Object.fromEntries(Object.entries(initial || {}).filter(([k, v]) => ['name', 'owner_name', 'phone', 'email', 'address', 'city', 'state', 'zip', 'website', 'social', 'notes'].includes(k) && typeof v === 'string')) }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const dup = useMemo(() => {
    const p = e164(f.phone);
    return p ? existing.find((x) => e164(x.phone) === p) : null;
  }, [f.phone, existing]);
  const save = async () => {
    if (!f.name.trim() || saving || dup) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const row = await insertProspect(client, {
        store_id: storeId,
        name: f.name.trim(),
        owner_name: f.owner_name.trim() || null,
        phone: e164(f.phone) || f.phone.trim() || null,
        email: f.email.trim() || null,
        address: f.address.trim() || null,
        city: f.city.trim() || null,
        state: f.state.trim() || null,
        zip: f.zip.trim() || null,
        website: f.website.trim() || null,
        social: f.social.trim() || null,
        notes: f.notes.trim() || null,
        rep_id: f.rep_id || null,
        source: 'manual',
        created_by: userEmail || null,
        created_at: now,
        updated_at: now,
        ...freshProspectFields(now),
      });
      notify(`${row.name} added. First call is due today.`);
      onAdded(row);
    } catch (e) {
      console.error('sales: add failed', e);
      notify(e?.code === '23505' ? 'That phone number is already on the list' : 'Could not add that store', 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title="Add a store" subtitle="One furniture store to sell to. The first call is due today." footer={
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        <button type="button" onClick={save} disabled={!f.name.trim() || saving || !!dup} className={btnPrimary}>{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add store</button>
      </div>
    }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Store name" className="sm:col-span-2"><input value={f.name} onChange={set('name')} className={inputCls} placeholder="Bay Area Furniture Gallery" autoFocus /></Field>
        <Field label="Owner"><input value={f.owner_name} onChange={set('owner_name')} className={inputCls} placeholder="First name is enough" /></Field>
        <Field label="Phone" hint={dup ? `Already on the list as ${dup.name}` : undefined}><input type="tel" value={f.phone} onChange={set('phone')} className={`${inputCls} ${dup ? 'border-red-300' : ''}`} placeholder="(555) 123-4567" /></Field>
        <Field label="Email" className="sm:col-span-2"><input type="email" value={f.email} onChange={set('email')} className={inputCls} placeholder="owner@store.com" /></Field>
        <Field label="Address" className="sm:col-span-2"><input value={f.address} onChange={set('address')} className={inputCls} placeholder="711 Railroad Ave" /></Field>
        <Field label="City"><input value={f.city} onChange={set('city')} className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="State"><input value={f.state} onChange={set('state')} className={inputCls} placeholder="CA" /></Field>
          <Field label="ZIP"><input value={f.zip} onChange={set('zip')} className={inputCls} /></Field>
        </div>
        <Field label="Website"><input value={f.website} onChange={set('website')} className={inputCls} placeholder="store.com" /></Field>
        <Field label="Facebook or Instagram"><input value={f.social} onChange={set('social')} className={inputCls} placeholder="@store or page link" /></Field>
        <Field label="Owned by" className="sm:col-span-2">
          <select value={f.rep_id} onChange={set('rep_id')} className={inputCls}>
            <option value="">Nobody yet</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <Field label="Notes" className="sm:col-span-2"><textarea value={f.notes} onChange={set('notes')} rows={3} className={inputCls} placeholder="How we found them, what we know" /></Field>
      </div>
    </Sheet>
  );
}
